
import React, { useState, useEffect } from "react";
import axios from "axios";
import pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import escudo from "./escudo";

pdfMake.vfs = pdfFonts.default?.vfs || pdfFonts.pdfMake?.vfs;

const NotasConPDF = ({ instanciaId }) => {
  const [materias, setMaterias] = useState([]);
  const [materiaSeleccionada, setMateriaSeleccionada] = useState(null);
  const [divisionSeleccionada, setDivisionSeleccionada] = useState(null);

  const [cargando, setCargando] = useState(false);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [error, setError] = useState("");

  const [pdfUrl, setPdfUrl] = useState(null);
  const [mostrarPDF, setMostrarPDF] = useState(false);
  const [nombrePDF, setNombrePDF] = useState("");

  // ==========================================
  // OBTENER NOTAS
  // ==========================================

  useEffect(() => {
    let activo = true;

    const fetchNotas = async () => {
      setCargando(true);
      setError("");
      setMateriaSeleccionada(null);
      setDivisionSeleccionada(null);

      try {
        const response = await axios.post(
          `https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/devuelveNotasPorInstancia?idInstancia=${instanciaId.id}`
        );

        if (activo) {
          setMaterias(
            Array.isArray(response.data) ? response.data : []
          );
        }
      } catch (error) {
        console.error("Error al obtener las notas:", error);

        if (activo) {
          setError("No se pudieron obtener las calificaciones.");
          setMaterias([]);
        }
      } finally {
        if (activo) {
          setCargando(false);
        }
      }
    };

    if (instanciaId?.id) {
      fetchNotas();
    } else {
      setMaterias([]);
      setCargando(false);
    }

    return () => {
      activo = false;
    };
  }, [instanciaId?.id]);

  // ==========================================
  // LIMPIAR URL DEL PDF
  // ==========================================

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // ==========================================
  // BLOQUEAR SCROLL DEL FONDO
  // CUANDO ESTÁ ABIERTO EL PDF
  // ==========================================

  useEffect(() => {
    if (!mostrarPDF) return;

    const overflowAnterior = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const cerrarConEscape = (e) => {
      if (e.key === "Escape") {
        setMostrarPDF(false);
      }
    };

    window.addEventListener("keydown", cerrarConEscape);

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener("keydown", cerrarConEscape);
    };
  }, [mostrarPDF]);

  // ==========================================
  // GENERAR PDF
  // ==========================================

  const generarPDF = (materia) => {
    if (generandoPDF) return;

    setGenerandoPDF(true);
    setError("");

    try {
      const alumnos = materia.alumnos || [];

      const fechaHoy = new Date().toLocaleDateString("es-AR");

      const tableBody = [
        [
          { text: "Nº", style: "tableHeaderGray" },
          { text: "APELLIDO", style: "tableHeaderGray" },
          { text: "NOMBRE", style: "tableHeaderGray" },
          { text: "D.N.I.", style: "tableHeaderGray" },
          { text: "Nota (En Nº)", style: "tableHeaderGray" },
        ],

        ...alumnos.map((alumno, index) => [
          {
            text: String(index + 1),
            alignment: "center",
          },
          {
            text: alumno.apellido || "",
          },
          {
            text: alumno.nombres || "",
          },
          {
            text: String(alumno.alumnoDni || ""),
            alignment: "center",
          },
          {
            text:
              alumno.nota !== null &&
              alumno.nota !== undefined
                ? String(alumno.nota)
                : "",
            alignment: "center",
          },
        ]),
      ];

      const escudoPasado = escudo();

      const docDefinition = {
        pageSize: "A4",
        pageOrientation: "portrait",
        pageMargins: [30, 45, 30, 45],

        footer: (currentPage, pageCount) => ({
          columns: [
            {
              text: `ISeP - Año ${new Date().getFullYear()}`,
              alignment: "left",
              margin: [30, 10, 0, 0],
            },
            {
              text: `Página ${currentPage} de ${pageCount}`,
              alignment: "right",
              margin: [0, 10, 30, 0],
            },
          ],
          fontSize: 8,
        }),

        content: [
          {
            image: escudoPasado,
            width: 75,
            alignment: "center",
            margin: [0, 0, 0, 8],
          },

          {
            text: "PLANILLA DE CALIFICACIÓN",
            style: "title",
            alignment: "center",
            margin: [0, 0, 0, 5],
          },

          {
            text: `${instanciaId.escuela || ""} - ISeP`,
            style: "title",
            alignment: "center",
            margin: [0, 0, 0, 10],
          },

          {
            text: instanciaId.nombreCurso || "",
            style: "title",
            margin: [0, 0, 0, 5],
          },

          {
            text: `${materia.division}º DIVISIÓN`,
            bold: true,
            margin: [0, 0, 0, 10],
          },

          {
            text: `Fecha: ${fechaHoy}`,
            alignment: "right",
            style: "subtitle",
            margin: [0, 0, 0, 8],
          },

          {
            text: `Materia: ${materia.nombreMateria || ""}`,
            margin: [0, 0, 0, 3],
          },

          {
            text: `Instancia: ${instanciaId.nombreInstancia || ""}`,
            margin: [0, 0, 0, 3],
          },

          {
            text: `Docente: ${materia.docente || ""}`,
            margin: [0, 0, 0, 8],
          },

          {
            style: "tableExample",

            table: {
              headerRows: 1,

              // Mantener encabezado en cada página
              dontBreakRows: true,

              widths: [22, 135, "*", 70, 65],

              body: tableBody,
            },

            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0 ? "#e0e0e0" : null,

              hLineWidth: () => 0.6,
              vLineWidth: () => 0.6,

              hLineColor: () => "#000",
              vLineColor: () => "#000",

              paddingLeft: () => 4,
              paddingRight: () => 4,
              paddingTop: () => 4,
              paddingBottom: () => 4,
            },
          },
        ],

        styles: {
          title: {
            fontSize: 13,
            bold: true,
          },

          subtitle: {
            fontSize: 9,
            italics: true,
          },

          tableHeaderGray: {
            bold: true,
            fontSize: 8,
            fillColor: "#e0e0e0",
            alignment: "center",
          },

          tableExample: {
            margin: [0, 5, 0, 15],
          },
        },

        defaultStyle: {
          fontSize: 8.5,
        },
      };

      const nombreArchivo = `Planilla_${String(
        materia.nombreMateria || "Materia"
      ).replace(/[^\w-]/g, "_")}_Division_${
        materia.division
      }.pdf`;

      pdfMake.createPdf(docDefinition).getBlob((blob) => {
        const url = URL.createObjectURL(blob);

        setPdfUrl(url);
        setNombrePDF(nombreArchivo);
        setMostrarPDF(true);
        setGenerandoPDF(false);
      });
    } catch (error) {
      console.error("Error al generar PDF:", error);
      setError("No se pudo generar la planilla PDF.");
      setGenerandoPDF(false);
    }
  };

  // ==========================================
  // DESCARGAR PDF
  // ==========================================

  const descargarPDF = () => {
    if (!pdfUrl) return;

    const enlace = document.createElement("a");

    enlace.href = pdfUrl;
    enlace.download = nombrePDF || "Planilla.pdf";

    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
  };

  // ==========================================
  // ABRIR EN OTRA PESTAÑA
  // ==========================================

  const abrirPDF = () => {
    if (!pdfUrl) return;

    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  // ==========================================
  // MATERIAS AGRUPADAS
  // ==========================================

  const materiasAgrupadas = [
    ...new Set(materias.map((m) => m.nombreMateria)),
  ];

  const divisionesDisponibles = materias.filter(
    (m) => m.nombreMateria === materiaSeleccionada
  );

  // ==========================================
  // ESTILOS
  // ==========================================

  const estiloBoton = (activo = false) => ({
    padding: "10px 18px",
    borderRadius: "8px",
    border: activo
      ? "1px solid #1976d2"
      : "1px solid #d5e3ef",
    backgroundColor: activo ? "#1976d2" : "#f3f8fc",
    color: activo ? "#fff" : "#1976d2",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "13px",
    transition: "0.2s",
  });

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div
      style={{
        padding: "20px",
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      {/* ENCABEZADO */}

      <div
        style={{
          textAlign: "center",
          marginBottom: "25px",
        }}
      >
        <h3 style={{ marginBottom: "5px" }}>
          Planillas de calificaciones
        </h3>

        <p style={{ color: "#666", margin: 0 }}>
          {instanciaId?.nombreInstancia || ""}
        </p>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: "#fce8e8",
            color: "#a52222",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "15px",
          }}
        >
          {error}
        </div>
      )}

      {cargando && (
        <div style={{ textAlign: "center", padding: "25px" }}>
          Cargando calificaciones...
        </div>
      )}

      {/* SELECCIÓN DE MATERIAS */}

      {!cargando && (
        <>
          <h5 style={{ marginBottom: "12px" }}>
            Seleccionar materia
          </h5>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "25px",
            }}
          >
            {materiasAgrupadas.map((nombre) => (
              <button
                key={nombre}
                onClick={() => {
                  setMateriaSeleccionada(nombre);
                  setDivisionSeleccionada(null);
                }}
                style={estiloBoton(
                  materiaSeleccionada === nombre
                )}
              >
                {nombre}
              </button>
            ))}
          </div>

          {materiasAgrupadas.length === 0 && !error && (
            <p style={{ color: "#777" }}>
              No hay materias disponibles.
            </p>
          )}
        </>
      )}

      {/* SELECCIÓN DE DIVISIONES */}

      {materiaSeleccionada && !cargando && (
        <div
          style={{
            borderTop: "1px solid #ddd",
            paddingTop: "20px",
            marginBottom: "25px",
          }}
        >
          <h5>
            Divisiones de {materiaSeleccionada}
          </h5>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              marginTop: "12px",
            }}
          >
            {divisionesDisponibles.map((m, index) => (
              <button
                key={`${m.division}-${index}`}
                onClick={() => setDivisionSeleccionada(m)}
                style={estiloBoton(
                  divisionSeleccionada === m
                )}
              >
                {m.division}º División
              </button>
            ))}
          </div>
        </div>
      )}

      {/* LISTADO DE ALUMNOS */}

      {divisionSeleccionada && (
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: "10px",
            padding: "18px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            width: "100%",
            boxSizing: "border-box",
            minWidth: 0,
          }}
        >
          <h4 style={{ marginBottom: "8px" }}>
            {divisionSeleccionada.nombreMateria} -{" "}
            {divisionSeleccionada.division}º División
          </h4>

          <p style={{ marginBottom: "5px" }}>
            <strong>Docente:</strong>{" "}
            {divisionSeleccionada.docente || "-"}
          </p>

          <p style={{ marginBottom: "15px" }}>
            <strong>Total de alumnos:</strong>{" "}
            {divisionSeleccionada.alumnos?.length || 0}
          </p>

          {/* TABLA CON SCROLL */}

          <div
            style={{
              maxHeight: "55vh",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #ddd",
              borderRadius: "6px",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table
              style={{
                width: "100%",
                minWidth: "550px",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: "13px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Nº",
                    "Apellido",
                    "Nombre",
                    "DNI",
                    "Nota",
                  ].map((titulo) => (
                    <th
                      key={titulo}
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        backgroundColor: "#e8f0f8",
                        padding: "10px",
                        borderBottom: "1px solid #ccc",
                        textAlign: "left",
                      }}
                    >
                      {titulo}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {(divisionSeleccionada.alumnos || []).map(
                  (alumno, index) => (
                    <tr key={`${alumno.alumnoDni}-${index}`}>
                      <td style={estiloCelda}>
                        {index + 1}
                      </td>

                      <td style={estiloCelda}>
                        {alumno.apellido || ""}
                      </td>

                      <td style={estiloCelda}>
                        {alumno.nombres || ""}
                      </td>

                      <td style={estiloCelda}>
                        {alumno.alumnoDni || ""}
                      </td>

                      <td
                        style={{
                          ...estiloCelda,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {alumno.nota !== null &&
                        alumno.nota !== undefined
                          ? alumno.nota
                          : "-"}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* BOTÓN GENERAR */}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "18px",
            }}
          >
            <button
              onClick={() => generarPDF(divisionSeleccionada)}
              disabled={generandoPDF}
              style={{
                backgroundColor: "#198754",
                color: "#fff",
                padding: "11px 22px",
                border: "none",
                borderRadius: "7px",
                cursor: generandoPDF
                  ? "wait"
                  : "pointer",
                fontWeight: "bold",
                opacity: generandoPDF ? 0.65 : 1,
              }}
            >
              {generandoPDF
                ? "Generando planilla..."
                : "Ver planilla PDF"}
            </button>
          </div>
        </div>
      )}

      {/* ===================================== */}
      {/* VISOR PDF EN MODAL                   */}
      {/* ===================================== */}

      {mostrarPDF && pdfUrl && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Visor de planilla PDF"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 99999,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "12px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "1150px",
              height: "calc(100dvh - 24px)",
              maxHeight: "100%",
              backgroundColor: "#fff",
              borderRadius: "10px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0,0,0,0.4)",
            }}
          >
            {/* BARRA SUPERIOR */}

            <div
              style={{
                backgroundColor: "#1976d2",
                color: "#fff",
                padding: "12px 16px",
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
                flexShrink: 0,
              }}
            >
              <div>
                <strong>Planilla de calificación</strong>

                <div
                  style={{
                    fontSize: "12px",
                    opacity: 0.85,
                    marginTop: "3px",
                    overflowWrap: "anywhere",
                  }}
                >
                  {divisionSeleccionada?.nombreMateria} -{" "}
                  {divisionSeleccionada?.division}º División
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <button
                  onClick={descargarPDF}
                  style={estiloBotonModal("#198754")}
                >
                  Descargar
                </button>

                <button
                  onClick={abrirPDF}
                  style={estiloBotonModal("#fff", "#1976d2")}
                >
                  Abrir aparte
                </button>

                <button
                  onClick={() => setMostrarPDF(false)}
                  style={estiloBotonModal("#dc3545")}
                >
                  Cerrar ✕
                </button>
              </div>
            </div>

            {/* VISOR CON SCROLL INDEPENDIENTE */}

            <div
              style={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflow: "hidden",
                backgroundColor: "#525659",
              }}
            >
              <iframe
                title="Vista previa de planilla de calificación"
                src={pdfUrl}
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// ESTILOS AUXILIARES
// ==========================================

const estiloCelda = {
  padding: "8px 10px",
  borderBottom: "1px solid #e5e5e5",
  verticalAlign: "middle",
};

const estiloBotonModal = (
  backgroundColor,
  color = "#fff"
) => ({
  backgroundColor,
  color,
  border: "none",
  borderRadius: "6px",
  padding: "8px 13px",
  fontSize: "12px",
  fontWeight: "bold",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

export default NotasConPDF;
